import { Container } from 'unstated'
import {
  checkForTakenSlots,
  cleanAndMergeSlots,
  createTempCalendarRange,
  createUnitCalendarSlot,
  getWeek,
  groupSlotsByDay,
  parseBlockingSlots,
  parseMarkedSlots,
  splitToFitInDays,
} from '../Helpers/Calendar'
import { notify } from '../../../Helpers/Notifications'
import { getCalendarData } from '../../../API/Course'

const initialState = () => ({
  loaded: false,
  now: new Date(),
  weekOffset: 0,
  currentWeek: [],
  marked: [],
  userBookings: [],
  teacherBookings: [],
  studentSlots: [],
  teacherSlots: [],
  studentApprovedSlots: [],
  studentPendingSlots: [],
  teacherAvailabilitySlots: [],
  pickedSlots: Array.from({ length: 7 }).map(() => []),
  picked: [],
  numberOfClasses: 5,
  mode: 'enrollment',
  slotDuration: 60,
  onChange: () => {},
  initData: {},
})

export default class CalendarStore extends Container {
  name = 'CalendarStore'

  state = initialState()

  clock = null

  init = async (livDemyID, slots, courseInfo, slotDuration) => {
    const { initData } = this.state

    if (
      livDemyID === initData.livDemyID &&
      slotDuration === initData.slotDuration &&
      initData.slots === slots
    ) {
      return
    }

    await this.setState({
      ...initialState(),
      picked: [],
      initData: { livDemyID, slots, courseInfo, slotDuration },
    })

    this.startClock()

    const calendarData = await getCalendarData(livDemyID)

    await Promise.all([this.setCurrentWeek(), this.parseSlots(calendarData)])

    await this.setState({
      loaded: true,
      numberOfClasses: slots,
      courseInfo,
      slotDuration,
    })
  }

  initMyCalendar = async (
    slots = {
      studentApprovedSlots: [],
      studentPendingSlots: [],
      teacherSlots: [],
      teacherAvailabilitySlots: [],
      tempMarked: [],
    },
    onChange = () => {},
  ) => {
    if (!slots) {
      return
    }

    await this.setState({
      ...initialState(),
      loaded: false,
      mode:
        slots.tempMarked && slots.tempMarked.length
          ? 'editAvailability'
          : 'myCalendar',
      onChange,
    })

    await this.setCurrentWeek()

    await this.setState({
      loaded: true,
      studentApprovedSlots: groupSlotsByDay(
        splitToFitInDays(slots.studentApprovedSlots),
      ),
      studentPendingSlots: groupSlotsByDay(
        splitToFitInDays(slots.studentPendingSlots),
      ),
      teacherSlots: groupSlotsByDay(splitToFitInDays(slots.teacherSlots)),
      teacherAvailabilitySlots: slots.teacherAvailabilitySlots,
      marked: parseMarkedSlots([]),
      tempMarked: slots.tempMarked || [],
    })
  }

  initTeacherMarkingCalendar = async (
    tempTeacherSlots,
    onChange = () => {},
  ) => {
    await this.setState({ ...initialState(), picked: [] })

    this.startClock()

    await this.setCurrentWeek()

    await this.setState({ onChange })

    await this.parseSlots({ tempTeacherSlots })

    await this.setState({
      loaded: true,
      mode: 'becomeTeacher',
    })
  }

  setCurrentWeek = async () => {
    const { now, weekOffset } = this.state

    const someDayOfTheWeek = new Date()
    someDayOfTheWeek.setDate(now.getDate() + weekOffset * 7)

    const currentWeek = getWeek(someDayOfTheWeek)

    await this.setState({ currentWeek })
  }

  changeWeek = async (type) => {
    await this.setState({
      weekOffset:
        type === 'next'
          ? this.state.weekOffset + 1
          : type === 'previous'
          ? Math.max(this.state.weekOffset - 1, 0)
          : 0,
    })

    await this.setCurrentWeek()
  }

  parseSlots = async (slotData) => {
    const { onChange } = this.state
    const {
      teacherSlots = [],
      teacherBookingSlots = [],
      bookings: userBookingSlots = [],
      tempTeacherSlots = [],
    } = slotData

    const marked = parseMarkedSlots(teacherSlots)
    const tempMarked = parseMarkedSlots(tempTeacherSlots)

    const userBookings = parseBlockingSlots(userBookingSlots, 'booking')

    const teacherBookings = parseBlockingSlots(
      teacherBookingSlots,
      'teachersBookings',
    )

    await this.setState({
      marked,
      tempMarked,
      userBookings,
      teacherBookings,
    })

    onChange(tempMarked)
  }

  startClock = () => {
    this.clock = setInterval(() => this.setState({ now: new Date() }), 10000)
  }

  pickSlot = async (day, index, _, range) => {
    const { picked, numberOfClasses, weekOffset } = this.state

    if (index === range.end - 1) {
      return
    }

    if (!weekOffset) {
      const today = new Date().getDay() - 1

      if (day < today) {
        notify('warning', "You can't pick a slot in the past!")
        return
      }
      if (day === today) {
        const hours = new Date().getHours()
        const minutes = new Date().getMinutes()

        const minIndex = hours * 2 + (minutes >= 30 ? 2 : 1)

        if (index < minIndex) {
          notify('warning', "You can't pick a slot in the past!")
          return
        }
      }
    }

    if (picked.length >= numberOfClasses) {
      notify(
        'warning',
        'The required number of slots have already been picked!',
      )
      return
    }

    if (!!picked.find((slot) => slot.weekOffset !== weekOffset)) {
      notify('warning', 'You cant pick the starting slots in different weeks.')
      return
    }

    if (
      picked.find(
        (slot) => slot.day === day && Math.abs(slot.index - index) <= 1,
      )
    ) {
      return
    }

    picked.push({ day, index, weekOffset })

    await this.setState({ picked })

    const success = await this.computePickedSlots()

    if (!success) {
      await this.setState({ picked: [] })
    }
  }

  isSlotAvailable = (day, index) => {
    const {
      picked: allPicked,
      numberOfClasses,
      marked,
      weekOffset,
      slotDuration,
    } = this.state
    const picked = allPicked.filter((slots) => slots.weekOffset === weekOffset)

    if (picked.length >= numberOfClasses) {
      return false
    } else if (
      picked.find(
        (slot) => slot.day === day && Math.abs(slot.index - index) <= 1,
      )
    ) {
      return false
    } else if (
      slotDuration !== 30 &&
      marked[day].find((range) => range.end - 1 === index)
    ) {
      return false
    } else {
      return true
    }
  }

  computePickedSlots = async () => {
    const {
      picked,
      userBookings,
      teacherBookings,
      numberOfClasses,
      now,
      weekOffset,
      slotDuration,
    } = this.state

    const pickedSlots = Array.from({ length: 7 }).map(() => [])

    if (!picked.length) {
      await this.setState({ pickedSlots })
      return
    }

    let classesLeft = numberOfClasses,
      iteration = 0

    while (classesLeft > 0) {
      const someDayOfTheWeek = new Date()
      someDayOfTheWeek.setDate(now.getDate() + (weekOffset + iteration) * 7)

      const currentWeek = getWeek(someDayOfTheWeek)

      let clashed = false

      picked.slice(0, classesLeft).forEach(({ day, index }) => {
        const slot = createUnitCalendarSlot(
          new Date(currentWeek[day].getTime()).setHours(0, index * 30, 0, 0),
          'pickedSlot',
          null,
          slotDuration * 60 * 1000,
        )

        const isClashing =
          checkForTakenSlots(teacherBookings, slot) ||
          checkForTakenSlots(userBookings, slot)

        if (isClashing) {
          notify(
            'warning',
            'The picked slot is clashing with other booked slots in some week',
          )
          clashed = true
          return false
        }

        pickedSlots[day].push(slot)
      })

      if (clashed) {
        return false
      }

      classesLeft -= picked.length
      iteration++
    }

    await this.setState({ pickedSlots })
    return true
  }

  removeSlot = async (day, index) => {
    const { picked, weekOffset } = this.state

    if (!!picked.find((slot) => slot.weekOffset !== weekOffset)) {
      notify('warning', 'You cant edit picked slots in the starting week only.')
      return
    }

    const existingIndex = picked.findIndex(
      (slot) => slot.day === day && Math.abs(slot.index - index) <= 1,
    )
    if (existingIndex !== -1) {
      picked.splice(existingIndex, 1)
    }
    await this.setState({ picked })
    await this.computePickedSlots()
  }

  getPickedSlots = () =>
    this.state.pickedSlots
      .reduce((merged, day) => [...merged, ...day], [])
      .map(({ exactStartTime, exactEndTime }) => ({
        startTime: exactStartTime,
        endTime: exactEndTime,
      }))

  onSlotPressed = async (day, index, marked) => {
    if (marked) {
      await this.setState({ removeSlots: true })
      this.removeSlotFromRange(day, index)
      return
    }
    const { tempMarked } = this.state

    const startTime = getWeek(new Date())[day]
    startTime.setHours(0, index * 30, 0, 0)

    const tempRange = createTempCalendarRange(startTime, 'availability')
    tempMarked[day].push(tempRange)

    await this.setState({ tempMarked, tempRange })
  }

  onSlotReleased = async (day, index) => {
    const { tempRange, removeSlots, onChange, tempMarked } = this.state

    if (removeSlots) {
      await this.setState({ tempRange: null, removeSlots: false })
      onChange(cleanAndMergeSlots(tempMarked))
      return
    }

    if (!tempRange) {
      return
    }

    await this.setState({ tempRange: null })
    const merged = cleanAndMergeSlots(tempMarked, { day, index })
    await this.setState({ tempMarked: merged })
    onChange(merged)
  }

  onSlotMove = async (day, index, marked) => {
    const { tempMarked, tempRange, removeSlots } = this.state

    if (removeSlots) {
      this.removeSlotFromRange(day, index)
      return
    }
    if (!tempRange) {
      return
    }

    if (index >= tempRange.initialEnd) {
      tempRange.start = tempRange.initialStart
      tempRange.end = index + 1
    } else if (index <= tempRange.initialStart) {
      tempRange.end = tempRange.initialEnd
      tempRange.start = index
    }

    tempMarked[day].map((range) => (range.temp ? tempRange : range))

    await this.setState({ tempRange, tempMarked })
  }

  removeSlotFromRange = async (day, index) => {
    const { tempMarked } = this.state
    const rangeIndex = tempMarked[day].findIndex(
      (range) => range.start <= index && range.end >= index,
    )
    if (rangeIndex === -1) {
      return
    }

    const range = tempMarked[day][rangeIndex]

    if (index === range.start) {
      tempMarked[day].splice(rangeIndex, 1, { ...range, start: index + 1 })
    } else if (index === range.end - 1) {
      tempMarked[day].splice(rangeIndex, 1, { ...range, end: index })
    } else {
      tempMarked[day].splice(
        rangeIndex,
        1,
        { ...range, end: index },
        { ...range, start: index + 1 },
      )
    }

    await this.setState({ tempMarked: cleanAndMergeSlots(tempMarked) })
  }

  reset = () => this.setState(initialState())
}
